// WebGL twinkle (Pixi v8 Filter) applied to a FIRE CUTOUT sprite — an RGBA image (tools/gen_firemask)
// whose alpha is an authored, fire-shaped mask of the painting's flames (transparent on stone/walls).
// The shader twinkles individual ART pixels (classic pixel-art fire shimmer) and PRESERVES the
// cutout's alpha. Because the cutout is fire-shaped, the animation can never touch stone and has no
// rectangular footprint. Scalar/vec2 uniforms only (these bind reliably in Pixi v8).
import { Filter, GlProgram, UniformGroup } from "pixi.js";

const VERT = `
in vec2 aPosition;
out vec2 vTextureCoord;
uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;
vec4 filterVertexPosition( void ) {
  vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
  position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
  return vec4(position, 0.0, 1.0);
}
vec2 filterTextureCoord( void ) {
  return aPosition * (uOutputFrame.zw * uInputSize.zw);
}
void main(void) {
  gl_Position = filterVertexPosition();
  vTextureCoord = filterTextureCoord();
}
`;

const FRAG = `
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform float uTime;
uniform vec2 uPixels;   // cutout size in ART pixels (for chunky per-pixel twinkle)

float hash(vec2 p){ p = fract(p * vec2(127.1, 311.7)); p += dot(p, p + 34.5); return fract(p.x * p.y); }

void main(void){
  vec2 uv = vTextureCoord;
  vec4 base = texture(uTexture, uv);
  if (base.a < 0.01) { finalColor = base; return; }   // outside the fire shape -> untouched
  float t = uTime;
  // COHESIVE FLICKER, BRIGHTEN-BIASED: fire flares up more than it dims (a uniform up/down pulse
  // looked "too dark" on the dips). Keep a high floor so it never goes murky.
  float f = (sin(t * 6.1) + 0.6 * sin(t * 9.7) + 0.45 * sin(t * 14.3)) * 0.488; // ~[-1,1]
  float pulse = 1.0 + 0.10 * f + 0.16 * max(0.0, f);   // min ~0.90, flares to ~1.16
  // PER-PIXEL SIZZLE, also brighten-biased: quantize to art pixels; each flame pixel pops ~10x/sec
  vec2 cell = floor(uv * uPixels);
  float s1 = floor(t * 10.0);
  float tw = (hash(cell + vec2(s1)) + hash(cell * 1.7 + vec2(s1 + 3.0))) * 0.5 - 0.5; // -0.5..0.5
  float sz = 1.0 + 0.28 * tw + 0.34 * max(0.0, tw);    // min ~0.86, bright pops up to ~1.45
  vec3 rgb = base.rgb * pulse * sz;
  rgb += vec3(0.45, 0.27, 0.09) * max(0.0, tw);        // warm flare on the bright sizzles
  finalColor = vec4(rgb, base.a);                       // keep the fire-shaped alpha
}
`;

export interface FireFilter extends Filter {
  setTime(seconds: number): void;
}

/** Twinkle filter for a fire-cutout sprite. pixelsW/H = the cutout's art-pixel size. */
export function createFireFilter(seed: number, pixelsW: number, pixelsH: number): FireFilter {
  const uniforms = new UniformGroup({
    uTime: { value: seed, type: "f32" }, // seed = phase offset so multiple fires differ
    uPixels: { value: new Float32Array([pixelsW, pixelsH]), type: "vec2<f32>" },
  });
  const filter = new Filter({
    glProgram: GlProgram.from({ vertex: VERT, fragment: FRAG }),
    resources: { fireUniforms: uniforms },
  }) as FireFilter;
  filter.setTime = (s: number) => { uniforms.uniforms.uTime = s + seed; };
  return filter;
}
